import React, { useEffect, useState } from 'react';
import {
	getAllTracks,
	uploadMediaReplace,
	uploadMediaNew,
	deleteMediaFile,
	getSettings,
	saveSettings,
	uploadFinalSlideImage,
	uploadFinalSlideTrack,
	deleteFinalSlideImage,
	deleteFinalSlideTrack,
} from './adminApi';
import './Admin.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const sectionTitles = { greeting: 'Приветствие', meditation: 'Медитация', ending: 'Завершение' };

export default function Media() {
	const [tracks, setTracks] = useState({ greeting: [], meditation: [], ending: [] });
	const [newTrackNames, setNewTrackNames] = useState({ greeting: '', meditation: '', ending: '' });
	const [loading, setLoading] = useState(true);
	const [confirmModal, setConfirmModal] = useState(null);
	const [zoomLink, setZoomLink] = useState('');
	const [zoomMeetingId, setZoomMeetingId] = useState('');
	const [zoomPasscode, setZoomPasscode] = useState('');
	const [zoomLinkSaving, setZoomLinkSaving] = useState(false);
	const [finalSlideImage, setFinalSlideImage] = useState(null);
	const [finalSlideTrack, setFinalSlideTrack] = useState(null);
	const [finalSlideUploading, setFinalSlideUploading] = useState(null);

	const applyTracksPayload = (data) => {
		setTracks({
			greeting: data.greeting || [],
			meditation: data.meditation || [],
			ending: data.ending || [],
		});
		setFinalSlideImage(data.finalSlideImage || null);
		setFinalSlideTrack(data.finalSlideTrack || null);
	};

	const refreshTracks = () =>
		getAllTracks()
			.then(applyTracksPayload)
			.catch((err) => console.error('Ошибка загрузки медиа:', err));

	useEffect(() => {
		refreshTracks().finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		getSettings()
			.then((data) => {
				setZoomLink(data.zoomLink || '');
				setZoomMeetingId(data.meetingId || '');
				setZoomPasscode(data.passcode || '');
				if (data.finalSlideImage !== undefined) setFinalSlideImage(data.finalSlideImage || null);
				if (data.finalSlideTrack !== undefined) setFinalSlideTrack(data.finalSlideTrack || null);
			})
			.catch((err) => console.error('Ошибка загрузки настроек:', err));
	}, []);

	useEffect(() => {
		return () => {
			if (confirmModal?.previewUrl) {
				URL.revokeObjectURL(confirmModal.previewUrl);
			}
		};
	}, [confirmModal]);

	const handleFileChange = (slot, e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const previewUrl = URL.createObjectURL(file);
		setConfirmModal({ slot, file, previewUrl });
		e.target.value = '';
	};

	const closeConfirmModal = () => {
		if (confirmModal?.previewUrl) {
			URL.revokeObjectURL(confirmModal.previewUrl);
		}
		setConfirmModal(null);
	};

	const handleConfirmSave = async () => {
		if (!confirmModal) return;
		const { slot, file } = confirmModal;
		setConfirmModal((prev) => (prev ? { ...prev, saving: true } : null));
		try {
			await uploadMediaReplace(slot.category, slot.filename, file);
			await refreshTracks();
			closeConfirmModal();
		} catch (err) {
			alert(err.message || 'Ошибка при замене файла');
			setConfirmModal((prev) => (prev ? { ...prev, saving: false } : null));
		}
	};

	const handleAddTrack = async (category, e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const displayName = (newTrackNames[category] || '').trim();

		try {
			await uploadMediaNew(category, file, displayName || undefined);
			await refreshTracks();
			setNewTrackNames((prev) => ({ ...prev, [category]: '' }));
		} catch (err) {
			alert(err.message || 'Ошибка при загрузке');
		}
		e.target.value = '';
	};

	const handleDeleteTrack = async (category, filename) => {
		if (!window.confirm(`Удалить файл «${filename}»?`)) return;
		try {
			await deleteMediaFile(category, filename);
			await refreshTracks();
		} catch (err) {
			alert(err.message || 'Ошибка при удалении');
		}
	};

	const handleFinalImage = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) return;
		setFinalSlideUploading('image');
		try {
			const res = await uploadFinalSlideImage(file);
			setFinalSlideImage(res.finalSlideImage || null);
			setFinalSlideTrack(res.finalSlideTrack || null);
		} catch (err) {
			alert(err.message || 'Ошибка загрузки');
		} finally {
			setFinalSlideUploading(null);
		}
	};

	const handleFinalTrack = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) return;
		setFinalSlideUploading('track');
		try {
			const res = await uploadFinalSlideTrack(file);
			setFinalSlideImage(res.finalSlideImage || null);
			setFinalSlideTrack(res.finalSlideTrack || null);
		} catch (err) {
			alert(err.message || 'Ошибка загрузки');
		} finally {
			setFinalSlideUploading(null);
		}
	};

	const handleRemoveFinalImage = async () => {
		if (!window.confirm('Удалить изображение финального слайда?')) return;
		try {
			const res = await deleteFinalSlideImage();
			setFinalSlideImage(res.finalSlideImage || null);
			setFinalSlideTrack(res.finalSlideTrack || null);
		} catch (err) {
			alert(err.message || 'Ошибка');
		}
	};

	const handleRemoveFinalTrack = async () => {
		if (!window.confirm('Удалить трек финального слайда?')) return;
		try {
			const res = await deleteFinalSlideTrack();
			setFinalSlideImage(res.finalSlideImage || null);
			setFinalSlideTrack(res.finalSlideTrack || null);
		} catch (err) {
			alert(err.message || 'Ошибка');
		}
	};

	const handleSaveZoomSettings = async () => {
		const link = zoomLink.trim();
		if (!link) {
			alert('Введите ссылку на Zoom');
			return;
		}
		setZoomLinkSaving(true);
		try {
			await saveSettings({
				zoomLink: link,
				zoomMeetingId: zoomMeetingId.trim(),
				zoomPasscode: zoomPasscode.trim(),
			});
			setZoomLink(link);
			setZoomMeetingId(zoomMeetingId.trim());
			setZoomPasscode(zoomPasscode.trim());
			alert('Настройки Zoom сохранены');
		} catch (err) {
			alert(err.message || 'Ошибка при сохранении');
		} finally {
			setZoomLinkSaving(false);
		}
	};

	if (loading) {
		return <div style={{ padding: 20 }}>Загрузка медиа…</div>;
	}

	const sections = ['greeting', 'meditation', 'ending'];

	return (
		<div style={{ padding: '20px 0' }}>
			<h3>Медиа</h3>
			<p style={{ color: '#666', marginBottom: 20 }}>
				Добавляйте и удаляйте аудиотреки по разделам. Замена файла сохраняет то же имя на сервере.
			</p>

			<div style={{ marginBottom: 32, padding: '16px', border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
				<h4 style={{ marginBottom: 12 }}>Настройки Zoom</h4>
				<p style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
					Используется в письмах при бронировании и в напоминании о сеансе.
				</p>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
					<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
						<label style={{ minWidth: 120 }}>Ссылка:</label>
						<input
							type="url"
							value={zoomLink}
							onChange={(e) => setZoomLink(e.target.value)}
							placeholder="https://us05web.zoom.us/j/..."
							style={{ flex: '1 1 300px', minWidth: 200, padding: '8px 12px', borderRadius: 5, border: '1px solid #ccc' }}
						/>
					</div>
					<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
						<label style={{ minWidth: 120 }}>Meeting ID:</label>
						<input
							type="text"
							value={zoomMeetingId}
							onChange={(e) => setZoomMeetingId(e.target.value)}
							placeholder="845 062 5388"
							style={{ flex: '1 1 200px', minWidth: 140, padding: '8px 12px', borderRadius: 5, border: '1px solid #ccc' }}
						/>
					</div>
					<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
						<label style={{ minWidth: 120 }}>Код доступа:</label>
						<input
							type="text"
							value={zoomPasscode}
							onChange={(e) => setZoomPasscode(e.target.value)}
							placeholder="t9H9BC"
							style={{ flex: '1 1 200px', minWidth: 140, padding: '8px 12px', borderRadius: 5, border: '1px solid #ccc' }}
						/>
					</div>
					<button
						type="button"
						className="btn_anketa"
						onClick={handleSaveZoomSettings}
						disabled={zoomLinkSaving}
						style={{ alignSelf: 'flex-start' }}
					>
						{zoomLinkSaving ? 'Сохранение…' : 'Сохранить'}
					</button>
				</div>
			</div>

			{sections.map((category) => {
				const list = tracks[category] || [];
				return (
					<div key={category} style={{ marginBottom: 32 }}>
						<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
							<h4 style={{ margin: 0 }}>{sectionTitles[category]}</h4>
							<div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
								<input
									type="text"
									value={newTrackNames[category]}
									onChange={(e) => setNewTrackNames((prev) => ({ ...prev, [category]: e.target.value }))}
									placeholder="Имя для отображения (например: приветствие 1)"
									style={{ minWidth: 260, padding: '8px 12px', borderRadius: 5, border: '1px solid #ccc' }}
								/>
								<label style={{ cursor: 'pointer', fontSize: 14, color: '#48b8c7', textDecoration: 'underline' }}>
									+ Добавить трек
									<input
										type="file"
										accept="audio/*"
										onChange={(e) => handleAddTrack(category, e)}
										style={{ display: 'none' }}
									/>
								</label>
							</div>
						</div>
						<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
							{list.length === 0 && (
								<p style={{ color: '#888', fontSize: 14 }}>Нет треков — добавьте файл.</p>
							)}
							{list.map((track) => {
								const slot = { category, filename: track.name };
								const key = `${category}/${track.name}`;
								const displayWithExt = track.name.includes('__')
									? track.name.split('__').slice(1).join('__')
									: track.name;
								const displayLabel = displayWithExt.replace(/\.[a-z0-9]+$/i, '');
								return (
									<div
										key={key}
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 16,
											flexWrap: 'wrap',
											padding: '12px',
											border: '1px solid #eee',
											borderRadius: 8,
											background: '#fafafa',
										}}
									>
										<div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200, flex: '1 1 200px' }}>
											<span style={{ fontWeight: 500 }}>{displayLabel}</span>
											<label style={{ cursor: 'pointer', fontSize: 12, color: '#48b8c7', textDecoration: 'underline' }}>
												Заменить файл
												<input
													type="file"
													accept="audio/*"
													onChange={(e) => handleFileChange(slot, e)}
													style={{ display: 'none' }}
												/>
											</label>
											<button
												type="button"
												className="btn_anketa"
												style={{ background: '#c0392b', fontSize: 12, padding: '4px 10px' }}
												onClick={() => handleDeleteTrack(category, track.name)}
											>
												Удалить
											</button>
										</div>
										{track.url && (
											<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
												<span style={{ fontSize: 18 }} title="Прослушать">🔊</span>
												<audio
													key={track.url}
													controls
													style={{ height: 32, maxWidth: 280 }}
													src={`${API_BASE_URL}${track.url}`}
												/>
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				);
			})}

			<div style={{ marginBottom: 32, padding: '16px', border: '1px solid #eee', borderRadius: 8, background: '#fafafa' }}>
				<h4 style={{ marginBottom: 8 }}>Финальный слайд</h4>
				<p style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
					Фото и трек для последнего шага в презентации (после слайда со всеми участниками). Хранится в настройках на сервере.
				</p>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					<div>
						<div style={{ fontWeight: 500, marginBottom: 8 }}>Изображение</div>
						{finalSlideImage && (
							<div style={{ marginBottom: 10 }}>
								<img
									src={`${API_BASE_URL}${finalSlideImage}`}
									alt=""
									style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 8, border: '1px solid #ddd' }}
								/>
							</div>
						)}
						<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
							<label style={{ cursor: 'pointer', fontSize: 14, color: '#48b8c7', textDecoration: 'underline' }}>
								{finalSlideImage ? 'Заменить изображение' : 'Загрузить изображение'}
								<input
									type="file"
									accept="image/*"
									disabled={finalSlideUploading === 'image'}
									onChange={handleFinalImage}
									style={{ display: 'none' }}
								/>
							</label>
							{finalSlideImage && (
								<button
									type="button"
									className="btn_anketa"
									style={{ background: '#c0392b', fontSize: 12, padding: '4px 10px' }}
									onClick={handleRemoveFinalImage}
									disabled={!!finalSlideUploading}
								>
									Удалить
								</button>
							)}
						</div>
					</div>
					<div>
						<div style={{ fontWeight: 500, marginBottom: 8 }}>Трек</div>
						{finalSlideTrack && (
							<audio
								key={finalSlideTrack}
								controls
								style={{ display: 'block', marginBottom: 10, maxWidth: '100%' }}
								src={`${API_BASE_URL}${finalSlideTrack}`}
							/>
						)}
						<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
							<label style={{ cursor: 'pointer', fontSize: 14, color: '#48b8c7', textDecoration: 'underline' }}>
								{finalSlideTrack ? 'Заменить трек' : 'Загрузить трек'}
								<input
									type="file"
									accept="audio/*"
									disabled={finalSlideUploading === 'track'}
									onChange={handleFinalTrack}
									style={{ display: 'none' }}
								/>
							</label>
							{finalSlideTrack && (
								<button
									type="button"
									className="btn_anketa"
									style={{ background: '#c0392b', fontSize: 12, padding: '4px 10px' }}
									onClick={handleRemoveFinalTrack}
									disabled={!!finalSlideUploading}
								>
									Удалить
								</button>
							)}
						</div>
					</div>
				</div>
			</div>

			{confirmModal && (
				<div className="modal-overlay" onClick={closeConfirmModal}>
					<div
						className="modal-content-media"
						onClick={(e) => e.stopPropagation()}
					>
						<h4 style={{ marginBottom: 16 }}>Подтверждение замены</h4>
						<p style={{ marginBottom: 12 }}>
							<strong>Файл:</strong> {confirmModal.file.name}
						</p>
						<p style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
							Будет сохранён как: {confirmModal.slot.filename}
						</p>
						<div style={{ marginBottom: 16 }}>
							<audio
								controls
								style={{ width: '100%', height: 36 }}
								src={confirmModal.previewUrl}
							/>
						</div>
						<div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
							<button
								type="button"
								className="btn_anketa"
								onClick={closeConfirmModal}
								disabled={confirmModal.saving}
								style={{ background: '#999' }}
							>
								Отмена
							</button>
							<button
								type="button"
								className="btn_anketa"
								onClick={handleConfirmSave}
								disabled={confirmModal.saving}
							>
								{confirmModal.saving ? 'Загрузка…' : 'Сохранить'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
